import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import { getLogoUrl } from '../../services/logoService';
import MobileMenuButton from './MobileMenuButton';
import MobileMenuOverlay from './MobileMenuOverlay';
import NotificationPanel from './NotificationPanel';

const PrimaryNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, isAuthenticated } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = {
    operations: [
      { path: '/dashboard', label: 'Dashboard', icon: 'LayoutDashboard', minRole: 'view_only' },
      { path: '/bid-management', label: 'Bids', icon: 'FileText', minRole: 'view_only' },
      { path: '/customer-management', label: 'Customers', icon: 'Users', minRole: 'view_only' },
    ],
    operationsPost: [
      { path: '/project-management', label: 'Projects', icon: 'Briefcase', minRole: 'view_only' },
      { path: '/portfolio', label: 'Portfolio', icon: 'LayoutGrid', minRole: 'view_only' },
      { path: '/analytics', label: 'Analytics & Reporting', icon: 'BarChart3', minRole: 'user' },
      { path: '/pm-dashboard', label: 'PM Dashboard', icon: 'LayoutDashboard', minRole: 'user' },
    ],
    team: [
      { path: '/resource-management', label: 'Resources', icon: 'Package', minRole: 'user' },
      { path: '/sub-contractors', label: 'Sub-Contractors', icon: 'HardHat', minRole: 'user' },
      { path: '/vendors', label: 'Vendors', icon: 'Store', minRole: 'user' },
    ],
    more: [
      { path: '/project-phases', label: 'Phases', icon: 'GitBranch', minRole: 'user' },
      { path: '/archive', label: 'Archive', icon: 'Archive', minRole: 'user' },
      { path: '/application-bug-tracker', label: 'Application Bug Tracker', icon: 'Bug', minRole: 'user' },
      { path: '/pwa-install-instructions', label: 'Install Mobile App', icon: 'Smartphone', minRole: 'view_only' },
      { path: '/mobile-dashboard', label: 'Mobile Dashboard', icon: 'LayoutGrid', minRole: 'view_only' }
    ],
    admin: [
      { path: '/team-administration', label: 'Site Administration', icon: 'Settings2', minRole: 'administrator' },
      { path: '/audit-log', label: 'Audit Log', icon: 'ClipboardList', minRole: 'administrator' },
      { path: '/prospect-management', label: 'Prospects', icon: 'Target', minRole: 'view_only' },
      { path: '/legal-content-management', label: 'Legal Content', icon: 'Scale', minRole: 'administrator' },
      { path: '/mobile-app-configuration', label: 'Mobile App Config', icon: 'Tablet', minRole: 'administrator' },
      { path: '/slide-management', label: 'Display Slides', icon: 'Monitor', minRole: 'administrator' }
    ],
    profile: [
      { path: '/user-profile', label: 'Profile', icon: 'User', minRole: 'view_only' }
    ]
  };

  const roleHierarchy = {
    'administrator': 3,
    'user': 2,
    'view_only': 1
  };

  const canAccessRoute = (minRole) => {
    if (!isAuthenticated || !userProfile) return false;
    const userRoleLevel = roleHierarchy?.[userProfile?.role] || 0;
    const requiredRoleLevel = roleHierarchy?.[minRole] || 0;
    return userRoleLevel >= requiredRoleLevel;
  };

  const filterItemsByRole = (items) => {
    return items?.filter(item => canAccessRoute(item?.minRole));
  };

  const isActive = (path) => location?.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setActiveDropdown(null);
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    const url = await getLogoUrl();
    setLogoUrl(url);
  };

  const visibleOperations = filterItemsByRole(navigationItems?.operations);
  const visibleOperationsPost = filterItemsByRole(navigationItems?.operationsPost);
  const visibleTeam = filterItemsByRole(navigationItems?.team);
  const visibleMore = filterItemsByRole(navigationItems?.more);
  const visibleAdmin = filterItemsByRole(navigationItems?.admin);

  const isTeamActive = navigationItems?.team?.some(item => isActive(item?.path));

  return (
    <nav className="primary-nav">
      <div className="primary-nav-container">
        <div className="primary-nav-brand">
          <div className={logoUrl ? "" : "primary-nav-logo"}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Application logo"
                className="h-12 w-auto object-contain"
              />
            ) : (
              <Icon name="HardHat" size={24} color="#FFFFFF" />
            )}
          </div>
          <span className="primary-nav-title">ConstructionPro</span>
        </div>

        <div className="primary-nav-menu" ref={dropdownRef}>
          {visibleOperations?.map((item) => {
            const showPipeAfter =
              item?.path === '/dashboard' ||
              item?.path === '/customer-management';
            return (
              <React.Fragment key={item?.path}>
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`primary-nav-item ${isActive(item?.path) ? 'active' : ''}`}
                >
                  {item?.label}
                </button>
                {showPipeAfter && (
                  <span className="text-gray-400 select-none px-0.5">|</span>
                )}
              </React.Fragment>
            );
          })}

          {/* Team dropdown - left of Projects */}
          {visibleTeam?.length > 0 && (
            <div className="primary-nav-dropdown">
              <button
                onClick={() => toggleDropdown('team')}
                className={`primary-nav-dropdown-trigger ${isTeamActive ? 'active' : ''}`}
              >
                <span>Team</span>
                <Icon
                  name={activeDropdown === 'team' ? 'ChevronUp' : 'ChevronDown'}
                  size={16}
                />
              </button>

              {activeDropdown === 'team' && (
                <div className="primary-nav-dropdown-content">
                  <div className="px-4 py-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      Team
                    </p>
                    {visibleTeam?.map((item) => (
                      <button
                        key={item?.path}
                        onClick={() => handleNavigation(item?.path)}
                        className={`primary-nav-dropdown-item ${isActive(item?.path) ? 'active' : ''}`}
                      >
                        <Icon name={item?.icon} size={16} className="mr-2" />
                        {item?.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {visibleOperationsPost?.map((item) => {
            const showPipeAfter = item?.path === '/portfolio';
            return (
              <React.Fragment key={item?.path}>
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`primary-nav-item ${isActive(item?.path) ? 'active' : ''}`}
                >
                  {item?.label}
                </button>
                {showPipeAfter && (
                  <span className="text-gray-400 select-none px-0.5">|</span>
                )}
              </React.Fragment>
            );
          })}

          {(visibleMore?.length > 0 || visibleAdmin?.length > 0) && (
            <div className="primary-nav-dropdown">
              <button
                onClick={() => toggleDropdown('more')}
                className="primary-nav-dropdown-trigger"
              >
                <span>Admin</span>
                <Icon 
                  name={activeDropdown === 'more' ? 'ChevronUp' : 'ChevronDown'} 
                  size={16} 
                />
              </button>

              {activeDropdown === 'more' && (
                <div className="primary-nav-dropdown-content">
                  {visibleMore?.length > 0 && (
                    <div className="px-4 py-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Operations
                      </p>
                      {visibleMore?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`primary-nav-dropdown-item ${isActive(item?.path) ? 'active' : ''}`}
                        >
                          <Icon name={item?.icon} size={16} className="mr-2" />
                          {item?.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {visibleAdmin?.length > 0 && (
                    <div className="px-4 py-2">
                      {visibleAdmin?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`primary-nav-dropdown-item ${isActive(item?.path) ? 'active' : ''}`}
                        >
                          <Icon name={item?.icon} size={16} className="mr-2" />
                          {item?.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="primary-nav-actions">
          <NotificationPanel />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleNavigation('/user-profile')}
            className={isActive('/user-profile') ? 'text-accent' : ''}
          >
            <Icon name="User" size={20} />
          </Button>
          <MobileMenuButton
            onClick={() => setMobileMenuOpen(true)}
            isOpen={mobileMenuOpen}
          />
        </div>
      </div>
      <MobileMenuOverlay
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </nav>
  );
};

export default PrimaryNavigation;